import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Card, Button, TextInput, Badge, Spinner, Modal, Label, Select } from 'flowbite-react';
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiRefresh,
  HiSearch,
  HiCheckCircle,
  HiXCircle,
  HiInformationCircle,
  HiExclamationCircle,
  HiLocationMarker,
  HiOfficeBuilding,
  HiX,
} from 'react-icons/hi';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import AdminLayout from '@/components/layout/AdminLayout';
import Pagination from '@/components/Pagination';

// Fix para los iconos de Leaflet en React
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface Tenant {
  id: string;
  uuid: string;
  name: string;
  address: string | null;
  latitude: string | null;
  longitude: string | null;
  type: 'personal' | 'familiar' | 'comunidad' | 'empresa';
  description: string | null;
  status: 'active' | 'inactive';
  is_active: boolean;
  users_count: number;
  created_at: string;
  updated_at: string;
}

// Componente para actualizar el centro y zoom del mapa cuando cambian las coordenadas
function MapUpdater({ center, zoom }: { center: [number, number]; zoom?: number }) {
  const map = useMap();
  useEffect(() => {
    if (zoom !== undefined) {
      map.setView(center, zoom);
    } else {
      map.setView(center, map.getZoom());
    }
  }, [center, zoom, map]);
  return null;
}

// Componente para manejar clicks en el mapa
function MapClickHandler({ onMapClick }: { onMapClick: (e: L.LeafletMouseEvent) => void }) {
  useMapEvents({
    click: onMapClick,
  });
  return null;
}

export default function AdminTenants() {
  const { userProfile } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [filteredTenants, setFilteredTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [messageModal, setMessageModal] = useState({ type: 'success' as 'success' | 'error' | 'info', title: '', message: '', show: false });

  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [displayedTenants, setDisplayedTenants] = useState<Tenant[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    description: '',
    type: 'comunidad' as 'personal' | 'familiar' | 'comunidad' | 'empresa',
    status: 'active' as 'active' | 'inactive',
    latitude: '',
    longitude: '',
  });
  const [mapCenter, setMapCenter] = useState<[number, number]>([-33.4489, -70.6693]); // Santiago, Chile por defecto
  const [mapZoom, setMapZoom] = useState<number>(10); // Zoom inicial
  const [markerPosition, setMarkerPosition] = useState<[number, number] | null>(null);
  const [geocoding, setGeocoding] = useState(false);
  const markerRef = useRef<L.Marker | null>(null);
  const geocodeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastGeocodedAddressRef = useRef<string>('');
  const manualPinAdjustmentRef = useRef<boolean>(false);

  // Función para capitalizar texto (primera letra de cada palabra)
  const capitalizeWords = (text: string): string => {
    return text
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Función para calcular similitud entre dos strings (0-1, donde 1 es idéntico)
  const calculateSimilarity = (str1: string, str2: string): number => {
    if (!str1 || !str2) return 0;
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    if (longer.length === 0) return 1.0;
    const distance = levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  };

  // Función de distancia de Levenshtein para calcular similitud
  const levenshteinDistance = (str1: string, str2: string): number => {
    const matrix: number[][] = [];
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[str2.length][str1.length];
  };

  const loadTenants = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTenants(data || []);
      setFilteredTenants(data || []);
    } catch (error: any) {
      console.error('Error loading tenants:', error);
      showMessage('error', 'Error', 'Error al cargar tenants: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile?.role === 'superadmin') {
      loadTenants();
    }
  }, [userProfile]);

  useEffect(() => {
    let filtered = tenants;
    if (searchTerm) {
      filtered = filtered.filter(
        (tenant) =>
          tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tenant.uuid.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tenant.address?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredTenants(filtered);
    setCurrentPage(1); // Resetear a la primera página cuando cambia el filtro
  }, [searchTerm, tenants]);

  // Calcular tenants a mostrar según paginación
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setDisplayedTenants(filteredTenants.slice(startIndex, endIndex));
  }, [filteredTenants, currentPage, itemsPerPage]);

  // Efecto para geocodificar automáticamente la dirección con debounce
  useEffect(() => {
    // Limpiar timeout anterior si existe
    if (geocodeTimeoutRef.current) {
      clearTimeout(geocodeTimeoutRef.current);
    }

    const addressTrimmed = formData.address.trim();

    // Solo geocodificar si:
    // 1. El modal está abierto
    // 2. Hay dirección con más de 5 caracteres
    // 3. La dirección ha cambiado desde la última geocodificación
    // 4. No se ha ajustado manualmente el pin recientemente (a menos que la dirección cambie mucho)
    if (
      showEditModal &&
      addressTrimmed.length > 5 &&
      addressTrimmed !== lastGeocodedAddressRef.current
    ) {
      // Si el usuario ajustó manualmente el pin, verificar si la dirección cambió significativamente
      if (manualPinAdjustmentRef.current && lastGeocodedAddressRef.current) {
        // Calcular similitud entre la dirección actual y la última geocodificada
        const similarity = calculateSimilarity(addressTrimmed.toLowerCase(), lastGeocodedAddressRef.current.toLowerCase());
        // Si la dirección es similar (>70%), no geocodificar automáticamente (usuario podría estar editando detalles menores)
        if (similarity > 0.7) {
          return;
        }
        // Si la dirección cambió significativamente (<70% similar), resetear el flag y permitir geocodificación
        manualPinAdjustmentRef.current = false;
      }

      // Esperar 1 segundo después de que el usuario deje de escribir
      geocodeTimeoutRef.current = setTimeout(async () => {
        // Solo geocodificar si la dirección sigue siendo diferente (el usuario podría haber cambiado más)
        if (addressTrimmed !== lastGeocodedAddressRef.current) {
          await geocodeAddress(addressTrimmed, true); // Silent = true para no mostrar mensajes en geocodificación automática
        }
      }, 1000);
    }

    // Cleanup
    return () => {
      if (geocodeTimeoutRef.current) {
        clearTimeout(geocodeTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.address, showEditModal]);

  const showMessage = (type: 'success' | 'error' | 'info', title: string, message: string) => {
    setMessageModal({ type, title, message, show: true });
  };

  const showConfirm = (message: string, action: () => void) => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setShowConfirmModal(true);
  };

  // Geocodificar dirección usando Nominatim (OpenStreetMap)
  const geocodeAddress = async (address: string, silent = false) => {
    if (!address.trim()) return;

    setGeocoding(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
        {
          headers: {
            'User-Agent': 'DomusSecura/1.0',
          },
        }
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);

        // Hacer zoom en la ubicación encontrada (zoom 17 para vista cercana de la dirección)
        setMapCenter([lat, lon]);
        setMapZoom(17); // Zoom cercano para mostrar la dirección encontrada
        setMarkerPosition([lat, lon]);

        setFormData((prev) => ({
          ...prev,
          latitude: lat.toString(),
          longitude: lon.toString(),
        }));
        // Actualizar la dirección geocodificada
        lastGeocodedAddressRef.current = address.trim();
        // Resetear el flag de ajuste manual ya que ahora tenemos coordenadas de la geocodificación
        manualPinAdjustmentRef.current = false;
      } else if (!silent) {
        // Solo mostrar mensaje si no es geocodificación silenciosa automática
        showMessage('info', 'Dirección no encontrada', 'No se pudo encontrar la ubicación. Puedes ajustar el pin manualmente en el mapa.');
      }
    } catch (error) {
      console.error('Error geocodificando:', error);
      if (!silent) {
        showMessage('error', 'Error', 'Error al buscar la ubicación. Puedes ajustar el pin manualmente en el mapa.');
      }
    } finally {
      setGeocoding(false);
    }
  };

  const openEditModal = (tenant?: Tenant) => {
    // Resetear flags
    manualPinAdjustmentRef.current = false;
    lastGeocodedAddressRef.current = '';

    if (tenant) {
      setEditingTenant(tenant);
      const lat = tenant.latitude ? parseFloat(tenant.latitude) : null;
      const lon = tenant.longitude ? parseFloat(tenant.longitude) : null;

      const tenantAddress = tenant.address || '';
      lastGeocodedAddressRef.current = tenantAddress;

      setFormData({
        name: tenant.name || '',
        address: tenantAddress,
        description: tenant.description || '',
        type: tenant.type || 'comunidad',
        status: tenant.status || 'active',
        latitude: tenant.latitude || '',
        longitude: tenant.longitude || '',
      });

      if (lat && lon) {
        setMapCenter([lat, lon]);
        setMapZoom(17); // Zoom cercano si hay coordenadas
        setMarkerPosition([lat, lon]);
      } else if (tenantAddress) {
        // Intentar geocodificar si hay dirección pero no coordenadas
        geocodeAddress(tenantAddress);
      } else {
        setMapCenter([-33.4489, -70.6693]);
        setMapZoom(10); // Zoom inicial
        setMarkerPosition(null);
      }
    } else {
      setEditingTenant(null);
      setFormData({
        name: '',
        address: '',
        description: '',
        type: 'comunidad',
        status: 'active',
        latitude: '',
        longitude: '',
      });
      setMapCenter([-33.4489, -70.6693]);
      setMapZoom(10); // Zoom inicial
      setMarkerPosition(null);
    }
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingTenant(null);
    setFormData({
      name: '',
      address: '',
      description: '',
      type: 'comunidad',
      status: 'active',
      latitude: '',
      longitude: '',
    });
    setMapCenter([-33.4489, -70.6693]);
    setMapZoom(10); // Zoom inicial
    setMarkerPosition(null);
    // Resetear flags al cerrar
    manualPinAdjustmentRef.current = false;
    lastGeocodedAddressRef.current = '';
    if (geocodeTimeoutRef.current) {
      clearTimeout(geocodeTimeoutRef.current);
    }
  };

  const handleMapClick = (e: L.LeafletMouseEvent) => {
    const { lat, lng } = e.latlng;
    setMarkerPosition([lat, lng]);
    // Limpiar timeout de geocodificación cuando el usuario hace click manualmente
    if (geocodeTimeoutRef.current) {
      clearTimeout(geocodeTimeoutRef.current);
    }
    // Marcar que el usuario ajustó manualmente el pin
    manualPinAdjustmentRef.current = true;
    setFormData({
      ...formData,
      latitude: lat.toString(),
      longitude: lng.toString(),
    });
  };

  const handleMarkerDragEnd = (e: L.DragEndEvent) => {
    const marker = e.target;
    const position = marker.getLatLng();
    setMarkerPosition([position.lat, position.lng]);
    // Limpiar timeout de geocodificación cuando el usuario arrastra el pin manualmente
    if (geocodeTimeoutRef.current) {
      clearTimeout(geocodeTimeoutRef.current);
    }
    // Marcar que el usuario ajustó manualmente el pin
    manualPinAdjustmentRef.current = true;
    setFormData({
      ...formData,
      latitude: position.lat.toString(),
      longitude: position.lng.toString(),
    });
  };

  const saveTenant = async () => {
    try {
      const updateData: any = {
        name: formData.name,
        address: formData.address || null,
        description: formData.description || null,
        type: formData.type,
        status: formData.status,
        is_active: formData.status === 'active',
      };

      // Agregar coordenadas si existen
      if (formData.latitude && formData.longitude) {
        updateData.latitude = formData.latitude;
        updateData.longitude = formData.longitude;
      }

      if (editingTenant) {
        const { error } = await supabase
          .from('tenants')
          .update(updateData)
          .eq('id', editingTenant.id);

        if (error) throw error;
        showMessage('success', 'Tenant Actualizado', 'El tenant ha sido actualizado exitosamente');
      } else {
        const { error } = await supabase
          .from('tenants')
          .insert({
            ...updateData,
            uuid: crypto.randomUUID(),
          });

        if (error) throw error;
        showMessage('success', 'Tenant Creado', 'El tenant ha sido creado exitosamente');
      }
      closeEditModal();
      await loadTenants();
    } catch (error: any) {
      console.error('Error saving tenant:', error);
      showMessage('error', 'Error', 'Error al guardar tenant: ' + (error.message || 'Error desconocido'));
    }
  };

  const deleteTenant = async (tenant: Tenant) => {
    showConfirm(
      `¿Estás seguro de que deseas eliminar el tenant "${tenant.name}"? Esta acción no se puede deshacer. Se eliminarán todas las relaciones asociadas.`,
      async () => {
        try {
          // Primero eliminar las relaciones en user_tenants
          const { error: relationError } = await supabase
            .from('user_tenants')
            .delete()
            .eq('tenant_id', tenant.id);

          if (relationError) {
            console.error('Error eliminando relaciones user_tenants:', relationError);
            // No fallar completamente, puede que no haya relaciones
          }

          // Actualizar usuarios que tengan este tenant como tenant_id (limpiar referencia)
          const { error: updateUsersError } = await supabase
            .from('users')
            .update({ tenant_id: null })
            .eq('tenant_id', tenant.id);

          if (updateUsersError) {
            console.error('Error actualizando usuarios:', updateUsersError);
            // No fallar completamente
          }

          // Ahora eliminar el tenant
          const { error, data } = await supabase
            .from('tenants')
            .delete()
            .eq('id', tenant.id)
            .select();

          if (error) {
            console.error('Error completo al eliminar tenant:', error);
            // Si el error es de permisos (RLS), mostrar mensaje específico
            if (error.code === '42501' || error.code === 'PGRST301' || error.message?.includes('permission denied') || error.message?.includes('policy') || error.message?.includes('row-level security')) {
              throw new Error('No tienes permisos para eliminar este tenant. Ejecuta el script SQL: docs/sql/add_tenant_delete_policy.sql');
            }
            throw error;
          }

          // Verificar que realmente se eliminó
          // Si no hay data, puede que RLS esté bloqueando silenciosamente
          if (!data || data.length === 0) {
            throw new Error('No se pudo eliminar el tenant. Verifica que tengas permisos (RLS). Ejecuta: docs/sql/add_tenant_delete_policy.sql');
          }

          showMessage('success', 'Tenant Eliminado', 'El tenant ha sido eliminado exitosamente');
          await loadTenants();
        } catch (error: any) {
          console.error('Error deleting tenant:', error);
          const errorMessage = error.message || error.code || 'Error desconocido';
          showMessage('error', 'Error al Eliminar', `No se pudo eliminar el tenant: ${errorMessage}. Verifica la consola para más detalles.`);
        } finally {
          setShowConfirmModal(false);
        }
      }
    );
  };

  const getTypeBadge = (type: string) => {
    const config = {
      personal: { color: 'blue' as const, label: 'Personal' },
      familiar: { color: 'purple' as const, label: 'Familiar' },
      comunidad: { color: 'green' as const, label: 'Comunidad' },
      empresa: { color: 'gray' as const, label: 'Empresa' },
    };
    const typeConfig = config[type as keyof typeof config] || config.comunidad;
    return <Badge color={typeConfig.color}>{typeConfig.label}</Badge>;
  };

  if (!userProfile || userProfile.role !== 'superadmin') {
    return (
      <AdminLayout>
        <Card>
          <div className="p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Acceso Denegado</h3>
            <p className="text-gray-600 dark:text-gray-400">No tienes permisos para acceder a esta página</p>
          </div>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Gestión de Tenants</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Administra los tenants del sistema</p>
          </div>
          <Button color="blue" onClick={() => openEditModal()} className="gap-2">
            <HiPlus className="w-4 h-4" />
            Nuevo Tenant
          </Button>
        </div>

        {/* Estadísticas - Compactas */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-white dark:bg-gray-800 py-2 px-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">Total</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">{tenants.length}</p>
              </div>
              <HiOfficeBuilding className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" />
            </div>
          </Card>
          <Card className="bg-white dark:bg-gray-800 py-2 px-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">Activos</p>
                <p className="text-lg font-bold text-green-600">
                  {tenants.filter((t) => t.status === 'active').length}
                </p>
              </div>
              <HiCheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 ml-2" />
            </div>
          </Card>
          <Card className="bg-white dark:bg-gray-800 py-2 px-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">Inactivos</p>
                <p className="text-lg font-bold text-red-600">
                  {tenants.filter((t) => t.status === 'inactive').length}
                </p>
              </div>
              <HiX className="w-5 h-5 text-red-600 flex-shrink-0 ml-2" />
            </div>
          </Card>
        </div>

        {/* Búsqueda y filtros */}
        <Card className="bg-white dark:bg-gray-800">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <TextInput
                  type="text"
                  placeholder="Buscar por nombre, UUID o dirección..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
                <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              <div className="flex gap-2">
                <Select
                  value={itemsPerPage.toString()}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="w-32"
                >
                  <option value="10">10 por página</option>
                  <option value="25">25 por página</option>
                  <option value="50">50 por página</option>
                  <option value="100">100 por página</option>
                </Select>
                <Button color="gray" onClick={loadTenants} size="sm" className="gap-2 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
                  <HiRefresh className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="xl" className="text-blue-600" />
          </div>
        ) : filteredTenants.length === 0 ? (
          <Card className="bg-white dark:bg-gray-800">
            <div className="py-12 text-center">
              <p className="text-gray-600 dark:text-gray-400 text-lg">No se encontraron tenants</p>
            </div>
          </Card>
        ) : (
          <>
            {/* Vista de Tabla - Desktop */}
            <Card className="bg-white dark:bg-gray-800 hidden lg:block">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-gray-500 dark:text-gray-400">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      <th scope="col" className="px-6 py-3">Nombre</th>
                      <th scope="col" className="px-6 py-3">UUID</th>
                      <th scope="col" className="px-6 py-3">Tipo</th>
                      <th scope="col" className="px-6 py-3">Dirección</th>
                      <th scope="col" className="px-6 py-3">Usuarios</th>
                      <th scope="col" className="px-6 py-3">Estado</th>
                      <th scope="col" className="px-6 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedTenants.map((tenant) => (
                      <tr key={tenant.id} className="border-b bg-white dark:border-gray-700 dark:bg-gray-800">
                        <td className="whitespace-nowrap px-6 py-4 font-medium text-gray-900 dark:text-white">{tenant.name}</td>
                        <td className="px-6 py-4 font-mono text-xs">{tenant.uuid}</td>
                        <td className="px-6 py-4">{getTypeBadge(tenant.type || 'comunidad')}</td>
                        <td className="px-6 py-4">{tenant.address || '-'}</td>
                        <td className="px-6 py-4">{tenant.users_count || 0}</td>
                        <td className="px-6 py-4">
                          <Badge color={tenant.status === 'active' ? 'success' : 'failure'}>
                            {tenant.status === 'active' ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <Button
                              onClick={() => openEditModal(tenant)}
                              size="sm"
                              color="gray"
                              className="gap-1 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                            >
                              <HiPencil className="w-3 h-3" />
                              Editar
                            </Button>
                            <Button
                              onClick={() => deleteTenant(tenant)}
                              size="sm"
                              color="failure"
                              className="gap-1"
                            >
                              <HiTrash className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Vista de Tarjetas - Móvil */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:hidden">
              {displayedTenants.map((tenant) => (
                <Card key={tenant.id} className="hover:shadow-lg transition-shadow bg-white dark:bg-gray-800">
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">{tenant.name}</h3>
                        <p className="text-xs font-mono text-gray-500 dark:text-gray-400 mt-1">{tenant.uuid}</p>
                      </div>
                      <div className="flex flex-col gap-1 ml-2">
                        {getTypeBadge(tenant.type || 'comunidad')}
                        <Badge color={tenant.status === 'active' ? 'success' : 'failure'}>
                          {tenant.status === 'active' ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm mb-3">
                      <div className="text-gray-600 dark:text-gray-400">
                        <strong className="text-gray-900 dark:text-white">Dirección:</strong> {tenant.address || '-'}
                      </div>
                      <div className="text-gray-600 dark:text-gray-400">
                        <strong className="text-gray-900 dark:text-white">Usuarios:</strong> {tenant.users_count || 0}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400 text-xs">
                        Creado: {new Date(tenant.created_at).toLocaleDateString('es-ES')}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <Button
                        onClick={() => openEditModal(tenant)}
                        size="sm"
                        color="gray"
                        className="flex-1 gap-1 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                      >
                        <HiPencil className="w-3 h-3" />
                        Editar
                      </Button>
                      <Button
                        onClick={() => deleteTenant(tenant)}
                        size="sm"
                        color="failure"
                        className="gap-1"
                      >
                        <HiTrash className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Paginación */}
            {filteredTenants.length > itemsPerPage && (
              <Card className="bg-white dark:bg-gray-800 p-0">
                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(filteredTenants.length / itemsPerPage)}
                  onPageChange={(page) => setCurrentPage(page)}
                  totalItems={filteredTenants.length}
                  itemsPerPage={itemsPerPage}
                  showInfo={true}
                />
              </Card>
            )}
          </>
        )}

        {/* Modal de Edición - Agrandado */}
        <Modal show={showEditModal} onClose={closeEditModal} size="4xl">
          <div className="p-6 bg-white dark:bg-gray-800">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {editingTenant ? 'Editar Tenant' : 'Nuevo Tenant'}
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Columna Izquierda - Formulario */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Nombre</Label>
                  <TextInput
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      const capitalized = capitalizeWords(e.target.value);
                      setFormData({ ...formData, name: capitalized });
                    }}
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="type">Tipo</Label>
                  <Select
                    id="type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as 'personal' | 'familiar' | 'comunidad' | 'empresa' })}
                    className="mt-1"
                  >
                    <option value="personal">Personal</option>
                    <option value="familiar">Familiar</option>
                    <option value="comunidad">Comunidad</option>
                    <option value="empresa">Empresa</option>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="address">Dirección</Label>
                  <div className="flex gap-2 mt-1">
                    <TextInput
                      id="address"
                      type="text"
                      value={formData.address}
                      onChange={(e) => {
                        const capitalized = capitalizeWords(e.target.value);
                        setFormData({ ...formData, address: capitalized });
                      }}
                      className="flex-1"
                      placeholder="Ingresa una dirección..."
                    />
                    <Button
                      type="button"
                      color="blue"
                      onClick={() => {
                        // Resetear flags y limpiar coordenadas para permitir geocodificación manual
                        manualPinAdjustmentRef.current = false;
                        lastGeocodedAddressRef.current = '';
                        if (geocodeTimeoutRef.current) {
                          clearTimeout(geocodeTimeoutRef.current);
                        }
                        setFormData((prev) => ({ ...prev, latitude: '', longitude: '' }));
                        geocodeAddress(formData.address.trim(), false);
                      }}
                      disabled={geocoding || !formData.address.trim()}
                      className="gap-2"
                    >
                      {geocoding ? (
                        <Spinner size="sm" />
                      ) : (
                        <>
                          <HiLocationMarker className="w-4 h-4" />
                          Buscar
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Ingresa una dirección y el mapa se actualizará automáticamente. También puedes hacer clic en "Buscar" manualmente o hacer clic directamente en el mapa para colocar el pin.
                  </p>
                </div>
                <div>
                  <Label htmlFor="description">Descripción</Label>
                  <TextInput
                    id="description"
                    type="text"
                    value={formData.description}
                    onChange={(e) => {
                      const capitalized = capitalizeWords(e.target.value);
                      setFormData({ ...formData, description: capitalized });
                    }}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="status">Estado</Label>
                  <Select
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                    className="mt-1"
                  >
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                  </Select>
                </div>
                {formData.latitude && formData.longitude && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      <strong className="text-gray-900 dark:text-white">Coordenadas:</strong> {formData.latitude}, {formData.longitude}
                    </p>
                  </div>
                )}
              </div>

              {/* Columna Derecha - Mapa */}
              <div className="space-y-2">
                <Label>Ubicación en el Mapa</Label>
                <div className="h-96 w-full rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                  <MapContainer
                    center={mapCenter}
                    zoom={mapZoom}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapClickHandler onMapClick={handleMapClick} />
                    {markerPosition && (
                      <Marker
                        position={markerPosition}
                        draggable={true}
                        eventHandlers={{
                          dragend: handleMarkerDragEnd,
                        }}
                        ref={markerRef}
                      />
                    )}
                    <MapUpdater center={markerPosition || mapCenter} zoom={mapZoom} />
                  </MapContainer>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Haz clic en el mapa para colocar el pin o arrastra el pin existente para ajustar la ubicación.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button color="blue" onClick={saveTenant}>
                {editingTenant ? 'Guardar Cambios' : 'Crear Tenant'}
              </Button>
              <Button color="gray" onClick={closeEditModal} className="text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>

        {/* Modal de Confirmación */}
        <Modal show={showConfirmModal} onClose={() => setShowConfirmModal(false)} size="md">
          <div className="p-6 bg-white dark:bg-gray-800 text-center">
            <HiExclamationCircle className="mx-auto mb-4 h-12 w-12 text-red-600 dark:text-red-400" />
            <h3 className="mb-5 text-lg font-normal text-gray-900 dark:text-gray-200">
              {confirmMessage || (confirmAction ? '¿Estás seguro de realizar esta acción?' : '')}
            </h3>
            <div className="flex justify-center gap-4">
              <Button
                color="failure"
                onClick={() => {
                  if (confirmAction) confirmAction();
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                  setConfirmMessage('');
                }}
                className="bg-red-600 hover:bg-red-700 text-white dark:bg-red-600 dark:hover:bg-red-700 dark:text-white font-semibold px-6"
              >
                Sí, estoy seguro
              </Button>
              <Button
                color="gray"
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                  setConfirmMessage('');
                }}
                className="text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                No, cancelar
              </Button>
            </div>
          </div>
        </Modal>

        {/* Modal de Mensaje */}
        <Modal show={messageModal.show} onClose={() => setMessageModal({ ...messageModal, show: false })} size="md">
          <div className="p-6 bg-white dark:bg-gray-800 text-center">
            {messageModal.type === 'success' && <HiCheckCircle className="mx-auto mb-4 h-12 w-12 text-green-500" />}
            {messageModal.type === 'error' && <HiXCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />}
            {messageModal.type === 'info' && <HiInformationCircle className="mx-auto mb-4 h-12 w-12 text-blue-500" />}
            <h3 className="mb-2 text-xl font-bold text-gray-900 dark:text-white">{messageModal.title}</h3>
            <p className="mb-5 text-base text-gray-500 dark:text-gray-400">{messageModal.message}</p>
            <Button color="blue" onClick={() => setMessageModal({ ...messageModal, show: false })}>
              Cerrar
            </Button>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
}
